import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import {
    DxChartModule,
    DxPieChartModule,
    DxButtonComponent,
    DxDateRangeBoxComponent,
    DxSelectBoxComponent,
} from 'devextreme-angular';
import {
    AccessType,
    AggregateType,
    AuthService,
    IMyScoreShiftQuery,
    ITrendingQuery,
    PermissionType,
    ScoreDto,
    YardCapacityScoreExportQuery,
    YardCapacityScoreQuery,
    YardCapacityScoreType,
    YardCapacityTrendingQuery,
    YardManagementClient,
} from '@services';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
    ChartDataPoint,
    DoughnutChartComponent,
} from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import notify from 'devextreme/ui/notify';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileUtils } from 'src/app/shared/file-utils';
import EnumUtils from 'src/app/shared/enum-utils';
import { TrendingComponent } from "src/app/components/library/trending/trending.component";
import { ScoreBreakdownChartComponent } from '../components/score-breakdown-chart/score-breakdown-chart.component';

export function yardCapacityPalette(): Record<string, string> {
    const getColor = (name: string) =>
        getComputedStyle(document.body).getPropertyValue(name).trim();

    return {
        ['Controllable']: getColor('--bs-success'),
        ['Uncontrollable']: getColor('--bs-danger'),
        ['Yard Spots Available']: getColor('--bs-secondary'),
    };
}

export function yardCapacityBreakdownPalette(): Record<string, string> {
    const getColor = (name: string) =>
        getComputedStyle(document.body).getPropertyValue(name).trim();

    return {
        ['Checked In']: getColor('--bs-success'),
        ['Ready to Unload']: getColor('--bs-danger'),
        ['Unloading']: getColor('--bs-secondary'),
        ['Shunt Move Pending']: getColor('--bs-warning'),
        ['Empty Required']: getColor('--bs-primary-text-emphasis'),
        ['Ready For Checkout']: getColor('--bs-primary-bg-subtle'),
        ['Loading']: getColor('--bs-primary'),
        ['Loaded']: getColor('--bs-primary-border-subtle'),
        ['Investigation']: getColor('--bs-warning'),
        ['Tags Required']: getColor('--bs-success'),
        ['Refused']: getColor('--bs-danger'),
        ['Empty Not Required']: getColor('--bs-primary'),
    };
}

export function yardCapacityChartScore(score: ScoreDto | undefined): ScoreDto {
    const { ['Checked Out']: _, ...chartTotals } = score?.totals ?? {};
    return new ScoreDto({ ...score, totals: chartTotals });
}

@Component({
    selector: 'app-yard-capacity-score',
    imports: [
    CommonModule,
    DxPieChartModule,
    DxButtonComponent,
    DxSelectBoxComponent,
    CapitalCasePipe,
    DoughnutChartComponent,
    DxChartModule,
    DxDateRangeBoxComponent,
    TrendingComponent,
    ScoreBreakdownChartComponent,
],
    providers: [CurrencyPipe],
    templateUrl: './yard-capacity-score.component.html',
})
export class YardCapacityScoreComponent implements OnInit, OnDestroy {
    private yardManagementClient = inject(YardManagementClient);
    protected authService = inject(AuthService);
    private destroyRef = inject(DestroyRef);

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    private readonly scoreQuery$ = new BehaviorSubject<IMyScoreShiftQuery>({
        from: this.fromDate,
        to: this.toDate,
    });

    yardCapacities: Observable<{
        [key in keyof typeof YardCapacityScoreType]?: ScoreDto;
    }> = this.scoreQuery$.pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((query) =>
            this.yardManagementClient
                .getYardCapacityScore(
                    new YardCapacityScoreQuery({ from: query.from, to: query.to })
                )
                .pipe(
                    catchError(() => {
                        notify('Failed to load yard capacities', 'error', 3000);
                        return EMPTY;
                    })
                )
        ),
        shareReplay(1)
    );

    mainPalette: Record<string, string> = {};
    palette: Record<string, string> = {};
    scoreData: ChartDataPoint[] = [];
    daysLookBack: number = 0;

    private readonly dateRange$ = new BehaviorSubject<[Date, Date]>([
        new Date(),
        new Date(),
    ]);

    readonly chartTypes = [
        YardCapacityScoreType.Controllable,
        YardCapacityScoreType.Uncontrollable,
    ];

    loadMainScoreData = (query: IMyScoreShiftQuery) => {
        this.scoreQuery$.next(query);
        return this.yardCapacities.pipe(
            map((scores) => scores[YardCapacityScoreType.MainScore]),
            tap((score) => {
                this.scoreData = Object.entries(score?.totals ?? {}).map(([argument, value]) => ({ argument, value }));
            }),
            map(yardCapacityChartScore),
        );
    };

    ngOnInit(): void {
        this.mainPalette = yardCapacityPalette();
        this.palette = yardCapacityBreakdownPalette();
    }

    ngOnDestroy(): void {
        this.dateRange$.complete();
    }

    onDateRangeChanged(e: any): void {
        if (e.value) {
            this.dateRangeFilterValue = e.value;
        }
    }

    loadTrendingData = (query: ITrendingQuery) => {
        const q = new YardCapacityTrendingQuery();
        q.init({...query});
        return this.yardManagementClient.getYardCapacityTrending(q);
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onExport(): void {
        const query = new YardCapacityScoreExportQuery({
            from: this.fromDate,
            to: this.toDate,
        });

        this.yardManagementClient
            .exportYrdCapacity(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }
}
